import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Stack,
  Link as MuiLink,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getBookings, manualCancelBookingById, markNoShow } from "../../api/bookings";
import TripSelect from "../../components/TripSelect";
import dayjs from "dayjs";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { enqueueSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getCityDisplay } from "../../utils/city";
import { isDefaultCompanyUser } from "../../utils/auth";

const STATUS_UK_TO_KEY = {
  Відбулася: "completed",
  Оплачено: "paid",
  Відбудеться: "scheduled",
  "Очікує оплату": "pending",
  "Обробка платежу": "processing",
  "Повернення коштів в процесі": "refunding",
  Повернено: "refunded",
  "Не оплачено": "not_paid",
  "Помилка оплати": "failed",
  Скасовано: "cancelled",
  "Час на оплату вийшов": "expired",
  "Не прибув на рейс": "no_show",
};

const STATUS_TABS = [
  { id: "all", value: "" },
  { id: "paid", value: "paid" },
  { id: "attention", value: "pending,processing,not_paid" },
  { id: "cancelled", value: "cancelled" },
  { id: "no_show", value: "no_show" },
  { id: "refunds", value: "refunding,refunded" },
  { id: "failed", value: "failed" },
];

const DEFAULT_DATE_FROM = () => dayjs().format("YYYY-MM-DD");
const DEFAULT_DATE_TO = () => dayjs().add(14, "day").format("YYYY-MM-DD");

const normalizeStatusKey = (status) =>
  STATUS_UK_TO_KEY[status] || String(status || "").toLowerCase();

const passengerSummary = (passengers, t) => {
  if (!passengers?.length) return t("admin.noPassengers");
  if (passengers.length <= 2) {
    return passengers.map((p) => `${p.name} ${p.surname}`.trim()).join(", ");
  }
  return t("admin.passengersCountShort", { count: passengers.length });
};

const formatTripDateTime = (booking) => {
  const dt = booking?.from?.departure_datetime;
  if (dt) {
    const parsed = dayjs(dt, "YYYY-MM-DD HH:mm");
    if (parsed.isValid()) return parsed.format("DD.MM.YYYY HH:mm");
  }
  if (booking?.date) {
    const parsed = dayjs(booking.date, "DD.MM.YYYY");
    if (parsed.isValid()) return parsed.format("DD.MM.YYYY");
  }
  return "—";
};

const detectDatePreset = (startDate, endDate) => {
  if (!startDate && !endDate) return "all";
  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
  const weekEnd = dayjs().add(6, "day").format("YYYY-MM-DD");
  const next30 = dayjs().add(30, "day").format("YYYY-MM-DD");
  const next14 = dayjs().add(14, "day").format("YYYY-MM-DD");

  if (startDate === today && endDate === today) return "today";
  if (startDate === tomorrow && endDate === tomorrow) return "tomorrow";
  if (startDate === today && endDate === weekEnd) return "week";
  if (startDate === today && endDate === next30) return "next30";
  if (startDate === today && endDate === next14) return "next14";
  return "custom";
};

const fundsHolderLabel = (holder, t) => {
  if (holder === "agent") return t("admin.fundsHolderAgent");
  if (holder === "carrier") return t("admin.fundsHolderCarrier");
  return t("admin.fundsHolderSv");
};

const BookingsPage = () => {
  const { t } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const showCompany = isDefaultCompanyUser();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [bookingToMarkNoShow, setBookingToMarkNoShow] = useState(null);
  const [noShowComment, setNoShowComment] = useState("");
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState(null);

  const hasUrlDates =
    searchParams.has("date_from") || searchParams.has("date_to");
  const hasDeepLinkFilters =
    searchParams.has("status") ||
    searchParams.has("created_from") ||
    searchParams.has("created_to") ||
    searchParams.has("search") ||
    searchParams.has("trip");
  // Fresh visit → next 14 days. Deep-links without dates → no trip-date constraint.
  const initialStartDate = hasUrlDates
    ? searchParams.get("date_from") || ""
    : hasDeepLinkFilters
      ? ""
      : DEFAULT_DATE_FROM();
  const initialEndDate = hasUrlDates
    ? searchParams.get("date_to") || ""
    : hasDeepLinkFilters
      ? ""
      : DEFAULT_DATE_TO();

  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [page, setPage] = useState(Number(searchParams.get("page") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(searchParams.get("page_size") || 10)
  );
  const [orderBy] = useState(
    searchParams.get("order_by") || "trip__departure_date"
  );
  const [order] = useState(searchParams.get("order") || "asc");
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [createdFrom, setCreatedFrom] = useState(
    searchParams.get("created_from") || ""
  );
  const [createdTo, setCreatedTo] = useState(searchParams.get("created_to") || "");
  const [selectedTrip, setSelectedTrip] = useState(
    searchParams.get("trip") ? { id: searchParams.get("trip") } : null
  );

  const searchTimeoutRef = useRef(null);

  const datePreset = useMemo(
    () => detectDatePreset(startDate, endDate),
    [startDate, endDate]
  );

  const activeStatusTab = useMemo(() => {
    const match = STATUS_TABS.find((tab) => tab.value === statusFilter);
    return match?.id || "all";
  }, [statusFilter]);

  const clearFilters = () => {
    setStatusFilter("");
    setSearchText("");
    setSelectedTrip(null);
    setStartDate(DEFAULT_DATE_FROM());
    setEndDate(DEFAULT_DATE_TO());
    setCreatedFrom("");
    setCreatedTo("");
    setPage(0);
  };

  const hasActiveFilters = () =>
    !!(
      statusFilter ||
      searchText ||
      selectedTrip ||
      createdFrom ||
      createdTo ||
      datePreset !== "next14"
    );

  useEffect(() => {
    const params = {
      search: searchText || "",
      status: statusFilter || "",
      trip: selectedTrip?.id || "",
      date_from: startDate || "",
      date_to: endDate || "",
      created_from: createdFrom || "",
      created_to: createdTo || "",
      page: page || 0,
      page_size: rowsPerPage,
      order_by: orderBy,
      order,
    };
    setSearchParams(params);
  }, [
    searchText,
    statusFilter,
    selectedTrip,
    startDate,
    endDate,
    createdFrom,
    createdTo,
    page,
    rowsPerPage,
    orderBy,
    order,
    setSearchParams,
  ]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchBookings();
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchText,
    statusFilter,
    selectedTrip,
    page,
    rowsPerPage,
    orderBy,
    order,
    startDate,
    endDate,
    createdFrom,
    createdTo,
  ]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: order === "asc" ? orderBy : `-${orderBy}`,
        ...(selectedTrip ? { trip: selectedTrip.id } : {}),
        ...(searchText ? { search: searchText } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(startDate ? { date_from: startDate } : {}),
        ...(endDate ? { date_to: endDate } : {}),
        ...(createdFrom ? { created_from: createdFrom } : {}),
        ...(createdTo ? { created_to: createdTo } : {}),
      };
      const res = await getBookings(params);
      setBookings(res.results || []);
      setTotalCount(res.count || 0);
    } catch (error) {
      console.error("Failed to fetch bookings", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, id) => {
    event.stopPropagation();
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const renderStatusChip = (status) => {
    const statusKey = normalizeStatusKey(status);
    const colorMap = {
      completed: "success",
      paid: "success",
      scheduled: "info",
      pending: "info",
      processing: "info",
      refunding: "warning",
      refunded: "warning",
      not_paid: "warning",
      failed: "error",
      cancelled: "error",
      expired: "warning",
      no_show: "warning",
    };
    const variantMap = {
      paid: "filled",
      refunding: "filled",
      failed: "filled",
      cancelled: "filled",
      no_show: "filled",
    };
    const knownKeys = [
      "paid",
      "pending",
      "processing",
      "not_paid",
      "cancelled",
      "expired",
      "no_show",
      "refunded",
      "refunding",
      "failed",
      "completed",
      "scheduled",
    ];
    const label = knownKeys.includes(statusKey)
      ? t(`status.${statusKey}`)
      : status;
    return (
      <Chip
        label={label}
        color={colorMap[statusKey] || "default"}
        variant={variantMap[statusKey] || "outlined"}
        size="small"
      />
    );
  };

  const renderPaymentStatuses = (booking) => {
    const legs = booking?.leg_statuses;
    if (!Array.isArray(legs) || legs.length < 2) {
      return renderStatusChip(booking?.payment_status_display || booking?.payment_status);
    }
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        {legs.map((leg) => (
          <Stack key={leg.leg_index} direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {leg.leg_index === 2 ? t("roundTrip.return") : t("roundTrip.outbound")}
            </Typography>
            {renderStatusChip(leg.payment_status_display || leg.payment_status)}
          </Stack>
        ))}
      </Stack>
    );
  };

  const paymentMethodLabel = (method) => {
    if (method === "card") return t("admin.paymentCard");
    if (method === "on_site") return t("admin.paymentOnSite");
    return method || "";
  };

  const applyDatePreset = (preset) => {
    const today = dayjs();
    switch (preset) {
      case "today":
        setStartDate(today.format("YYYY-MM-DD"));
        setEndDate(today.format("YYYY-MM-DD"));
        break;
      case "tomorrow": {
        const tomorrow = today.add(1, "day");
        setStartDate(tomorrow.format("YYYY-MM-DD"));
        setEndDate(tomorrow.format("YYYY-MM-DD"));
        break;
      }
      case "week":
        setStartDate(today.format("YYYY-MM-DD"));
        setEndDate(today.add(6, "day").format("YYYY-MM-DD"));
        break;
      case "next30":
        setStartDate(today.format("YYYY-MM-DD"));
        setEndDate(today.add(30, "day").format("YYYY-MM-DD"));
        break;
      case "all":
        setStartDate("");
        setEndDate("");
        break;
      default:
        break;
    }
    setPage(0);
  };

  const handleStatusTabChange = (_event, tabId) => {
    const tab = STATUS_TABS.find((item) => item.id === tabId);
    setStatusFilter(tab?.value || "");
    setPage(0);
  };

  const openBooking = (bookingId) => {
    navigate(`/administrator/bookings/${bookingId}`);
  };

  const getStatusTabLabel = (tabId) => {
    const labels = {
      all: t("admin.statusTabAll"),
      paid: t("status.paid"),
      attention: t("admin.needsAttention"),
      cancelled: t("status.cancelled"),
      no_show: t("status.no_show"),
      refunds: t("admin.statusTabRefunds"),
      failed: t("status.failed"),
    };
    return labels[tabId] || tabId;
  };

  const filterChips = [];
  if (statusFilter) {
    filterChips.push({
      key: "status",
      label: `${t("admin.status")}: ${getStatusTabLabel(activeStatusTab)}`,
      onDelete: () => {
        setStatusFilter("");
        setPage(0);
      },
    });
  }
  if (datePreset !== "next14" && datePreset !== "all" && (startDate || endDate)) {
    const dateLabels = {
      today: t("admin.today"),
      tomorrow: t("admin.tomorrow"),
      week: t("admin.thisWeek"),
      next30: t("admin.next30Days"),
      custom: t("admin.tripDateRangeChip", {
        from: startDate ? dayjs(startDate).format("DD.MM.YYYY") : "…",
        to: endDate ? dayjs(endDate).format("DD.MM.YYYY") : "…",
      }),
      next14: t("admin.next14Days"),
    };
    filterChips.push({
      key: "dates",
      label: `${t("admin.tripDate")}: ${dateLabels[datePreset] || dateLabels.custom}`,
      onDelete: () => {
        setStartDate(DEFAULT_DATE_FROM());
        setEndDate(DEFAULT_DATE_TO());
        setPage(0);
      },
    });
  }
  if (createdFrom || createdTo) {
    filterChips.push({
      key: "created",
      label: t("admin.createdRange", {
        from: createdFrom ? dayjs(createdFrom).format("DD.MM.YYYY") : "…",
        to: createdTo ? dayjs(createdTo).format("DD.MM.YYYY") : "…",
      }),
      onDelete: () => {
        setCreatedFrom("");
        setCreatedTo("");
        setPage(0);
      },
    });
  }
  if (selectedTrip) {
    filterChips.push({
      key: "trip",
      label: t("admin.tripFilterChip", { id: selectedTrip.id }),
      onDelete: () => {
        setSelectedTrip(null);
        setPage(0);
      },
    });
  }
  if (searchText) {
    filterChips.push({
      key: "search",
      label: `${t("admin.search")}: ${searchText}`,
      onDelete: () => {
        setSearchText("");
        setPage(0);
      },
    });
  }

  const emptyMessage = () => {
    if (datePreset === "today") return t("admin.emptyToday");
    if (hasActiveFilters()) return t("admin.emptyFiltered");
    return t("admin.emptyBookings");
  };

  const renderPrimarySecondary = (primary, secondary) => (
    <Box>
      <Typography variant="body2" fontWeight={600} noWrap>
        {primary}
      </Typography>
      {secondary ? (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );

  const renderActionsMenu = (booking) => {
    const paymentKey =
      normalizeStatusKey(booking?.payment_status_display) ||
      booking?.payment_status;
    const tripKey =
      normalizeStatusKey(booking?.trip_status_display) ||
      normalizeStatusKey(booking?.trip_status);

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, booking.id)}
          aria-label={t("admin.options")}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEls[booking.id]}
          open={Boolean(menuAnchorEls[booking.id])}
          onClose={(e) => {
            e?.stopPropagation?.();
            handleMenuClose(booking.id);
          }}
        >
          {booking?.buyer_phone && (
            <MenuItem
              component="a"
              href={`tel:${booking.buyer_phone}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ListItemIcon>
                <PhoneIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("admin.call")}</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              openBooking(booking.id);
            }}
            sx={{ color: "primary.main" }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>{t("admin.view")}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              const link = `${window.location.origin}/booking/${booking.id}`;
              navigator.clipboard
                .writeText(link)
                .then(() => {
                  enqueueSnackbar(t("admin.linkCopied"), { variant: "success" });
                })
                .catch(() => {
                  enqueueSnackbar(t("admin.copyLinkError"), { variant: "error" });
                });
              handleMenuClose(booking.id);
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("admin.copyLink")}</ListItemText>
          </MenuItem>
          {paymentKey !== "cancelled" && paymentKey !== "no_show" && (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setBookingToCancel(booking.id);
                handleMenuClose(booking.id);
                setCancelDialogOpen(true);
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <CancelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{t("admin.cancel")}</ListItemText>
            </MenuItem>
          )}
          {tripKey === "completed" &&
            paymentKey !== "no_show" &&
            paymentKey !== "cancelled" && (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setBookingToMarkNoShow(booking.id);
                  handleMenuClose(booking.id);
                  setNoShowDialogOpen(true);
                }}
                sx={{ color: "warning.main" }}
              >
                <ListItemIcon>
                  <CancelIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText>{t("admin.markNoShow")}</ListItemText>
              </MenuItem>
            )}
        </Menu>
      </>
    );
  };

  const renderBookingCells = (booking) => {
    const routeLabel = `${getCityDisplay(booking?.from?.city) || "—"} — ${
      getCityDisplay(booking?.to?.city) || "—"
    }`;
    const tripSecondary = [
      formatTripDateTime(booking),
      normalizeStatusKey(booking?.trip_status) === "completed"
        ? t("status.completed")
        : normalizeStatusKey(booking?.trip_status) === "scheduled"
          ? t("status.scheduled")
          : null,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <>
        <TableCell>
          {renderPrimarySecondary(
            `#${booking.id}`,
            [
              passengerSummary(booking.passengers, t),
              booking.is_round_trip ? t("search.roundTrip") : null,
              booking.is_transfer ? t("admin.withTransfer") : null,
              showCompany ? booking.company : null,
            ]
              .filter(Boolean)
              .join(" · ")
          )}
        </TableCell>
        <TableCell>{renderPrimarySecondary(routeLabel, tripSecondary)}</TableCell>
        <TableCell>
          {renderPrimarySecondary(
            booking.buyer_phone ? (
              <MuiLink
                href={`tel:${booking.buyer_phone}`}
                onClick={(e) => e.stopPropagation()}
                underline="hover"
              >
                {booking.buyer_phone}
              </MuiLink>
            ) : (
              "—"
            ),
            booking.buyer_email || null
          )}
        </TableCell>
        <TableCell>
          <Box>
            {renderPaymentStatuses(booking)}
            {booking.payment_method ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {paymentMethodLabel(booking.payment_method)}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        <TableCell>{fundsHolderLabel(booking.funds_holder, t)}</TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {booking.price != null ? t("units.uahAmount", { amount: booking.price }) : "—"}
          </Typography>
          {booking.partner_price != null &&
          booking.partner_currency &&
          String(booking.partner_currency).toUpperCase() !== "UAH" ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {t("units.agentPrice", {
                amount: booking.partner_price,
                currency: booking.partner_currency,
              })}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          {renderActionsMenu(booking)}
        </TableCell>
      </>
    );
  };

  const renderMobileCard = (booking) => {
    const routeLabel = `${getCityDisplay(booking?.from?.city) || "—"} — ${
      getCityDisplay(booking?.to?.city) || "—"
    }`;
    return (
      <Paper
        key={booking.id}
        variant="outlined"
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
        onClick={() => openBooking(booking.id)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={700}>
              #{booking.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[
                passengerSummary(booking.passengers, t),
                booking.is_round_trip ? t("search.roundTrip") : null,
                booking.is_transfer ? t("admin.withTransfer") : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            {showCompany && (
              <Typography variant="caption" color="text.secondary">
                {booking.company}
              </Typography>
            )}
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>{renderActionsMenu(booking)}</Box>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={1}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {routeLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTripDateTime(booking)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Box>
              {booking.buyer_phone && (
                <MuiLink
                  href={`tel:${booking.buyer_phone}`}
                  onClick={(e) => e.stopPropagation()}
                  variant="body2"
                >
                  {booking.buyer_phone}
                </MuiLink>
              )}
              {booking.buyer_email && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {booking.buyer_email}
                </Typography>
              )}
            </Box>
            <Box textAlign="right">
              {renderPaymentStatuses(booking)}
              <Typography variant="caption" color="text.secondary" display="block">
                {fundsHolderLabel(booking.funds_holder, t)}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                {booking.price != null ? t("units.uahAmount", { amount: booking.price }) : "—"}
              </Typography>
              {booking.partner_price != null &&
              booking.partner_currency &&
              String(booking.partner_currency).toUpperCase() !== "UAH" ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {t("units.agentPrice", {
                amount: booking.partner_price,
                currency: booking.partner_currency,
              })}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container sx={{ mt: 2, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          mb={2}
        >
          <Box>
            <MuiLink
              component={RouterLink}
              to="/administrator/dashboard"
              underline="hover"
              variant="body2"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <ArrowBackIcon fontSize="inherit" />
              {t("admin.backToDashboard")}
            </MuiLink>
            <Typography variant="h4" sx={{ mb: 0 }}>
              {t("admin.bookingsList")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate("/administrator/bookings/new")}
              sx={{ borderRadius: 2 }}
            >
              {t("admin.newBooking")}
            </Button>
          </Stack>
        </Box>

        <TextField
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          placeholder={t("admin.searchPlaceholder")}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeStatusTab}
            onChange={handleStatusTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {STATUS_TABS.map((tab) => (
              <Tab key={tab.id} value={tab.id} label={getStatusTabLabel(tab.id)} />
            ))}
          </Tabs>
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gap={1.5}
          mb={2}
        >
          <Box sx={{ width: "100%", maxWidth: "100%" }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={["today", "tomorrow", "week", "next30", "all"].includes(datePreset) ? datePreset : null}
              onChange={(_e, value) => {
                if (value) applyDatePreset(value);
              }}
              sx={{
                flexWrap: "wrap",
                width: "100%",
                "& .MuiToggleButton-root": {
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                },
                "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                  marginLeft: 0,
                  borderLeft: "1px solid",
                  borderLeftColor: "divider",
                },
              }}
            >
              <ToggleButton value="today">{t("admin.today")}</ToggleButton>
              <ToggleButton value="tomorrow">{t("admin.tomorrow")}</ToggleButton>
              <ToggleButton value="week">{t("admin.thisWeek")}</ToggleButton>
              <ToggleButton value="next30">{t("admin.next30Days")}</ToggleButton>
              <ToggleButton value="all">{t("admin.allDates")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <DatePicker
              label={t("admin.tripDateFrom")}
              value={startDate ? dayjs(startDate) : null}
              format="DD.MM.YYYY"
              onChange={(newValue) => {
                setStartDate(newValue ? newValue.format("YYYY-MM-DD") : "");
                setPage(0);
              }}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: { width: { sm: 160 } } },
              }}
            />
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <DatePicker
              label={t("admin.tripDateTo")}
              value={endDate ? dayjs(endDate) : null}
              format="DD.MM.YYYY"
              onChange={(newValue) => {
                setEndDate(newValue ? newValue.format("YYYY-MM-DD") : "");
                setPage(0);
              }}
              slotProps={{
                textField: { size: "small", fullWidth: true, sx: { width: { sm: 160 } } },
              }}
            />
          </Box>

          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={(e) => setMoreFiltersAnchor(e.currentTarget)}
            variant={createdFrom || createdTo || selectedTrip ? "contained" : "outlined"}
          >
            {t("admin.moreFilters")}
          </Button>
          {selectedTrip?.id ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/administrator/trips/${selectedTrip.id}/passengers`)}
            >
              {t("admin.tripPassengersTitle")}
            </Button>
          ) : null}
        </Box>

        <Popover
          open={Boolean(moreFiltersAnchor)}
          anchorEl={moreFiltersAnchor}
          onClose={() => setMoreFiltersAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, width: { xs: 300, sm: 400 } }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("admin.moreFilters")}
            </Typography>
            <Stack spacing={2}>
              <DatePicker
                label={t("admin.createdFrom")}
                value={createdFrom ? dayjs(createdFrom) : null}
                format="DD.MM.YYYY"
                onChange={(newValue) => {
                  setCreatedFrom(newValue ? newValue.format("YYYY-MM-DD") : "");
                  setPage(0);
                }}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <DatePicker
                label={t("admin.createdTo")}
                value={createdTo ? dayjs(createdTo) : null}
                format="DD.MM.YYYY"
                onChange={(newValue) => {
                  setCreatedTo(newValue ? newValue.format("YYYY-MM-DD") : "");
                  setPage(0);
                }}
                slotProps={{ textField: { fullWidth: true, size: "small" } }}
              />
              <TripSelect
                value={selectedTrip}
                onChange={(newTrip) => {
                  setSelectedTrip(newTrip);
                  setPage(0);
                }}
                dateFrom={new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)}
                fullWidth
              />
            </Stack>
          </Box>
        </Popover>

        {filterChips.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2} alignItems="center">
            {filterChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} size="small" />
            ))}
            {hasActiveFilters() && (
              <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} color="error">
                {t("admin.clearFilters")}
              </Button>
            )}
          </Stack>
        )}

        {isMobile ? (
          <Box>
            {loading ? (
              [...Array(rowsPerPage)].map((_, idx) => (
                <Skeleton key={idx} variant="rounded" height={120} sx={{ mb: 1.5 }} />
              ))
            ) : bookings.length > 0 ? (
              bookings.map(renderMobileCard)
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
                <Typography color="text.secondary" gutterBottom>
                  {emptyMessage()}
                </Typography>
                {hasActiveFilters() && (
                  <Button onClick={clearFilters}>{t("admin.clearFilters")}</Button>
                )}
              </Paper>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.bookingColumn")}</TableCell>
                  <TableCell>{t("admin.tripColumn")}</TableCell>
                  <TableCell>{t("admin.contacts")}</TableCell>
                  <TableCell>{t("admin.status")}</TableCell>
                  <TableCell>{t("admin.fundsHolder")}</TableCell>
                  <TableCell>{t("admin.amount")}</TableCell>
                  <TableCell align="right">{t("admin.options")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(rowsPerPage)].map((_, idx) => (
                    <TableRow key={idx}>
                      {Array.from({ length: 7 }).map((_, cellIdx) => (
                        <TableCell key={cellIdx}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : bookings.length > 0 ? (
                  bookings.map((booking) => (
                    <TableRow
                      key={booking.id}
                      hover
                      onClick={() => openBooking(booking.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      {renderBookingCells(booking)}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary" gutterBottom>
                        {emptyMessage()}
                      </Typography>
                      {hasActiveFilters() && (
                        <Button onClick={clearFilters}>{t("admin.clearFilters")}</Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={t("admin.rowsPerPage")}
        />

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>{t("admin.cancelConfirmDialogTitle")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.cancelConfirmTitle", { id: bookingToCancel })}
              <br />
              {t("admin.cancelConfirmNote")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>{t("booking.cancel")}</Button>
            <Button
              onClick={async () => {
                try {
                  await manualCancelBookingById(bookingToCancel);
                  enqueueSnackbar(t("booking.cancelled"), { variant: "success" });
                  fetchBookings();
                } catch (err) {
                  enqueueSnackbar(err?.response?.data?.error || t("admin.cancelError"), {
                    variant: "error",
                  });
                } finally {
                  setCancelDialogOpen(false);
                  setBookingToCancel(null);
                }
              }}
              color="error"
              variant="contained"
            >
              {t("booking.cancelBooking")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={noShowDialogOpen}
          onClose={() => {
            setNoShowDialogOpen(false);
            setNoShowComment("");
            setBookingToMarkNoShow(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("admin.markNoShow")}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>{t("admin.markNoShowDesc")}</DialogContentText>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={noShowComment}
              onChange={(e) => setNoShowComment(e.target.value)}
              placeholder={t("admin.commentPlaceholder")}
              label={t("admin.comment")}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setNoShowDialogOpen(false);
                setNoShowComment("");
                setBookingToMarkNoShow(null);
              }}
            >
              {t("booking.cancel")}
            </Button>
            <Button
              color="warning"
              variant="contained"
              disabled={markingNoShow}
              onClick={async () => {
                try {
                  setMarkingNoShow(true);
                  await markNoShow(bookingToMarkNoShow, noShowComment);
                  enqueueSnackbar(t("booking.markedNoShow"), { variant: "success" });
                  fetchBookings();
                  setNoShowDialogOpen(false);
                  setNoShowComment("");
                  setBookingToMarkNoShow(null);
                } catch (err) {
                  const errorMsg =
                    err?.response?.data?.error || t("admin.markNoShowError");
                  enqueueSnackbar(errorMsg, { variant: "error" });
                } finally {
                  setMarkingNoShow(false);
                }
              }}
            >
              {t("admin.confirm")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default BookingsPage;
